import { useEffect, useState, useCallback } from "react";

/** 极简 hash router，无外部依赖。
 *  路由格式：#/path?query  -> { path: "/path", query: {...} } */
export type Route = { path: string; query: Record<string, string> };

function parse(): Route {
  const raw = window.location.hash.slice(1) || "/";
  const [p, q = ""] = raw.split("?");
  const query: Record<string, string> = {};
  new URLSearchParams(q).forEach((v, k) => (query[k] = v));
  return { path: p || "/", query };
}

export function useRoute(): Route {
  const [r, setR] = useState<Route>(parse);
  useEffect(() => {
    const on = () => setR(parse());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return r;
}

export function navigate(path: string, query?: Record<string, string | number | undefined>) {
  let url = path;
  if (query) {
    const sp = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => { if (v !== undefined && v !== "") sp.set(k, String(v)); });
    const s = sp.toString();
    if (s) url += "?" + s;
  }
  window.location.hash = url;
}

export function Link({ to, query, className, children }: {
  to: string; query?: Record<string, string | number | undefined>;
  className?: string; children: React.ReactNode;
}) {
  // 把 query 序列化成稳定的 key，避免每次渲染都重建 onClick。
  const queryKey = query ? JSON.stringify(query) : "";
  const onClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to, query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, queryKey]);
  return <a href={`#${to}`} onClick={onClick} className={className}>{children}</a>;
}

export function match(pattern: string, path: string): Record<string, string> | null {
  const ps = pattern.split("/").filter(Boolean);
  const xs = path.split("/").filter(Boolean);
  if (ps.length !== xs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i]!;
    const x = xs[i]!;
    if (p.startsWith(":")) params[p.slice(1)] = decodeURIComponent(x);
    else if (p !== x) return null;
  }
  return params;
}
